import React, { useState, useCallback, useEffect } from 'react';
import { withFreshData } from '../services/apiHeaders';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { costExplorerService } from '../services/costExplorerService';
import { getActiveTenantId } from '../services/tenantContext';
import { formatCurrency } from '../utils/currency';
import {
  CostExplorerQuery,
  CostDimension,
  TimeGranularity,
  CostView,
  CostFilter,
  SortConfig,
  SortDirection,
  DrillDownContext,
} from '../types/costExplorer';
import { CostChart } from '../components/costExplorer/CostChart';
import { DimensionBreakdown } from '../components/costExplorer/DimensionBreakdown';
import { FilterPanel } from '../components/costExplorer/FilterPanel';
import { ExportButton } from '../components/costExplorer/ExportButton';
import { DateRangePicker } from '../components/costExplorer/DateRangePicker';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import { ProviderTiles } from '../components/common/ProviderTiles';
import { GroupedDropdown } from '../components/common/GroupedDropdown';
import { Button } from '../components/common/Button';
import { StatCard } from '../components/common/StatCard';
import { Card } from '../components/common/Card';
import { DollarSign, Calendar, TrendingUp, TrendingDown, ArrowRightLeft, RefreshCw } from 'lucide-react';

/**
 * Provider-specific terminology for the "Account"/"Region"/"Tag" dimensions -
 * each cloud uses its own real term for these in its own billing console
 * (confirmed against AWS Cost Explorer, Azure Cost Management, and GCP
 * Billing Reports docs), so the label changes with the selected provider
 * instead of showing one generic word for all three.
 */
const PROVIDER_TERMS: Record<string, { account: string; region: string; tag: string }> = {
  all: { account: 'Account', region: 'Region', tag: 'Tag' },
  aws: { account: 'Linked Account', region: 'Region', tag: 'Tag' },
  azure: { account: 'Subscription', region: 'Location', tag: 'Tag' },
  gcp: { account: 'Project', region: 'Location', tag: 'Label' },
};


/**
 * Cost Explorer Page
 * Implements Requirement 77: Web UI — Cost Explorer [MVP]
 * 
 * AC 1: Multi-dimensional group-by with drill-down
 * AC 2: Time granularity (hourly, daily, weekly, monthly, quarterly)
 * AC 3: Filtering, sorting, and export (CSV, Parquet, FOCUS)
 * AC 4: Multiple cost views (unblended, blended, amortized, net-amortized, effective)
 * 
 * WCAG 2.1 AA compliant
 */
export const CostExplorerPage: React.FC = () => {
  // Date range state (draft — not applied until user clicks Apply)
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return [start, end];
  });
  const [startDate, endDate] = dateRange;

  // AC 2: Time granularity selector
  const [granularity, setGranularity] = useState<TimeGranularity>(TimeGranularity.DAILY);

  // Cloud Provider — separate top-level filter
  const [selectedProvider, setSelectedProvider] = useState<string>('all');

  // AC 1: Multi-dimensional group-by
  const [selectedDimension, setSelectedDimension] = useState<CostDimension>(CostDimension.SERVICE);
  const [drillDownPath, setDrillDownPath] = useState<DrillDownContext[]>([]);

  // AC 3: Filtering and sorting
  const [filters, setFilters] = useState<CostFilter[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'cost',
    direction: SortDirection.DESC,
  });

  // AC 4: Cost view selector
  const [costView, setCostView] = useState<CostView>(CostView.AMORTIZED);

  // Cost allocation tags for "Group By Tag" options - reloads when page becomes visible
  const [costAllocationTags, setCostAllocationTags] = useState<string[]>([]);
  
  // Function to load active cost allocation tags
  const loadCostAllocationTags = () => {
    const COST_API_URL = import.meta.env.VITE_COST_API_URL
      ? `${import.meta.env.VITE_COST_API_URL}/api/v1`
      : 'http://localhost:3002/api/v1';
    console.log('[CostExplorer] Loading cost allocation tags from:', `${COST_API_URL}/tagging/cost-allocation-tags`);
    const token = localStorage.getItem('accessToken');
    axios.get(`${COST_API_URL}/tagging/cost-allocation-tags`, {
      headers: {
        'x-tenant-id': getActiveTenantId(),
        'x-user-id': 'user-123',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      timeout: 5000,
    }).then(res => {
      console.log('[CostExplorer] Received cost allocation tags:', res.data);
      const data = res.data?.data;
      if (data) {
        // Collect all active tag keys across all providers
        const activeTags = new Set<string>();
        for (const provider of Object.values(data) as any[]) {
          for (const tag of provider.costAllocationTags || []) {
            if (tag.status === 'Active') {
              activeTags.add(tag.tagKey);
            }
          }
        }
        console.log('[CostExplorer] Active tags found:', [...activeTags]);
        setCostAllocationTags([...activeTags].sort());
      }
    }).catch((err) => {
      console.error('[CostExplorer] Error loading cost allocation tags:', err);
    });
  };
  
  // Load tags on mount
  useEffect(() => {
    loadCostAllocationTags();
  }, []);
  
  // Reload tags when page becomes visible (user navigates back from Tagging page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadCostAllocationTags();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also listen for focus event (when user switches back to this tab)
    window.addEventListener('focus', loadCostAllocationTags);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', loadCostAllocationTags);
    };
  }, []);

  // --- Applied state: only updated when "Apply" is clicked ---
  const [appliedFilters, setAppliedFilters] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
      granularity: TimeGranularity.DAILY,
      selectedProvider: 'all',
      selectedDimension: CostDimension.SERVICE,
      filters: [] as CostFilter[],
      sortConfig: { field: 'cost', direction: SortDirection.DESC } as SortConfig,
      costView: CostView.AMORTIZED,
    };
  });

  // Track whether filters have been applied at least once
  const [hasApplied, setHasApplied] = useState(true);

  const handleApply = useCallback(() => {
    if (!startDate || !endDate) return;
    setAppliedFilters({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      granularity,
      selectedProvider,
      selectedDimension,
      filters,
      sortConfig,
      costView,
    });
    setHasApplied(true);
  }, [startDate, endDate, granularity, selectedProvider, selectedDimension, filters, sortConfig, costView]);

  // Build filters including provider filter from APPLIED state
  const allFilters: CostFilter[] = [...appliedFilters.filters];
  if (appliedFilters.selectedProvider !== 'all') {
    allFilters.push({
      dimension: CostDimension.CLOUD,
      operator: 'equals' as CostFilter['operator'],
      values: [appliedFilters.selectedProvider],
    });
  }

  // Build query from APPLIED state
  const query: CostExplorerQuery = {
    startDate: appliedFilters.startDate,
    endDate: appliedFilters.endDate,
    granularity: appliedFilters.granularity,
    groupBy: {
      dimensions: [appliedFilters.selectedDimension],
    },
    filters: allFilters,
    sort: appliedFilters.sortConfig,
    costView: appliedFilters.costView,
  };

  // Fetch cost data — only when applied
  const {
    data: costData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['costExplorer', query],
    queryFn: () => costExplorerService.getCostData(query),
    staleTime: 60000, // 1 minute
    enabled: hasApplied,
  });

  // Fetch cost summary — only when applied. Includes appliedFilters.filters
  // (drill-down filters) so the Total Cost/Avg Daily Cost/Projected Monthly
  // tiles narrow along with the chart/breakdown - previously this query
  // never received them at all, so drilling into any dimension row
  // (tag, resource, service, region, account) left these tiles showing the
  // unfiltered total while the rest of the page correctly narrowed.
  const { data: costSummary } = useQuery({
    queryKey: ['costSummary', appliedFilters.startDate, appliedFilters.endDate, appliedFilters.costView, appliedFilters.selectedProvider, appliedFilters.filters],
    queryFn: () => costExplorerService.getCostSummary(appliedFilters.startDate, appliedFilters.endDate, appliedFilters.costView, appliedFilters.selectedProvider, appliedFilters.filters),
    staleTime: 60000,
    enabled: hasApplied,
  });

  const formatCurrencyValue = (value: number) => {
    // Get currency from cost summary if available, otherwise default to USD
    const currency = (costSummary?.currency as 'USD' | 'INR') || 'USD';
    
    // Use our custom currency formatter to ensure correct symbols
    return formatCurrency(value, currency, false);
  };

  const getDimensionLabel = (dim: CostDimension | string): string => {
    // A cost-allocation-tag dropdown option's value is the literal string
    // "tag:<Key>" (see the dimension dropdown below), not a CostDimension
    // enum member - handle that before the static lookup.
    if (dim.startsWith('tag:')) {
      return `Tag: ${dim.slice(4)}`;
    }
    const terms = PROVIDER_TERMS[selectedProvider] || PROVIDER_TERMS.all;
    const labels: Record<CostDimension, string> = {
      [CostDimension.CLOUD]: 'Cloud Provider',
      [CostDimension.ACCOUNT]: terms.account,
      [CostDimension.SERVICE]: 'Service',
      [CostDimension.REGION]: terms.region,
      [CostDimension.TAG]: terms.tag,
      [CostDimension.APPLICATION]: 'Application',
      [CostDimension.BUSINESS_UNIT]: 'Business Unit',
      [CostDimension.ENVIRONMENT]: 'Environment',
      [CostDimension.CUSTOM]: 'Resource',
    };
    return labels[dim as CostDimension] ?? 'Dimension';
  };

  const getCostViewLabel = (view: CostView): string => {
    const labels: Record<CostView, string> = {
      [CostView.UNBLENDED]: 'Unblended',
      [CostView.BLENDED]: 'Blended',
      [CostView.AMORTIZED]: 'Amortized',
      [CostView.NET_AMORTIZED]: 'Net Amortized',
      [CostView.EFFECTIVE]: 'Effective',
      // Named for what it is rather than the CUR column, because this is the
      // one a finance reader wants and "net unblended" means nothing to them.
      [CostView.NET_UNBLENDED]: 'Net (Billed Amount)',
    };
    return labels[view];
  };

  /**
   * Rebuild the filter list from a drill-down path.
   *
   * Filters were previously kept in lockstep with the path BY INDEX, which
   * assumed every filter came from a drill-down. The filter panel lets you add
   * your own, so clicking a breadcrumb could silently delete a filter you set
   * by hand. Rebuilding by dimension instead keeps hand-added filters on other
   * dimensions and replaces only the drill-down ones.
   */
  const syncFiltersToPath = (path: DrillDownContext[]): CostFilter[] => {
    const pathDimensions = new Set<string>(path.map((c) => String(c.dimension)));
    const userFilters = filters.filter(
      (f) =>
        !pathDimensions.has(String(f.dimension)) &&
        // A filter on the dimension currently being drilled is a drill-down
        // leftover even when that dimension is no longer in the path (the case
        // where we just navigated out of it), so it must not be preserved.
        String(f.dimension) !== String(drilledDimension)
    );
    const pathFilters: CostFilter[] = path.map((c) => ({
      dimension: c.dimension,
      operator: 'equals' as CostFilter['operator'],
      values: [c.value],
    }));
    return [...userFilters, ...pathFilters];
  };

  /**
   * The dimension a click on the chart actually drills into.
   *
   * It is the APPLIED dimension, not the dropdown's current value. Those differ
   * whenever Group By has been changed without pressing Apply - the chart below
   * is still showing services while the dropdown reads "Region", and recording
   * the click as a Region step would label an EC2 bar as a region.
   */
  const drilledDimension = appliedFilters.selectedDimension;

  /**
   * Apply a drill-down immediately instead of waiting for the Apply button.
   *
   * Drill-down wrote to the PENDING filter state only, so clicking a service
   * moved the breadcrumb while every number underneath stayed put until Apply
   * was pressed separately. A breadcrumb that says you are inside AmazonEC2
   * above figures for the whole estate is worse than no drill-down at all.
   * Clicking a bar is an explicit navigation, so it applies on the spot - which
   * is also how AWS Cost Explorer and Azure Cost Management behave.
   */
  const applyDrillFilters = (nextFilters: CostFilter[]) => {
    setFilters(nextFilters);
    setAppliedFilters((prev) => ({ ...prev, filters: nextFilters }));
    setHasApplied(true);
  };

  /**
   * A drill-down path is a PATH, so it holds at most one step per dimension.
   *
   * This used to append unconditionally. Clicking through services therefore
   * grew the breadcrumb without bound and with duplicates - confirmed live as
   * "All / AmazonEC2 / AmazonS3 / Azure: Virtual Machines / AmazonEC2 /
   * AmazonRDS / ..." - which is not a path at all, just a click history. Worse,
   * each click also stacked another equals-filter on the SAME dimension, and
   * "service = EC2 AND service = S3" matches nothing, so the numbers below the
   * breadcrumb were being narrowed to an impossible intersection.
   *
   * Clicking a second service is a sideways move, not a deeper one: it replaces
   * the current Service step. Going deeper means changing Group By to another
   * dimension first, which appends a genuine second step (Service -> Region).
   */
  const handleDrillDown = (dimensionValue: string) => {
    const existingIndex = drillDownPath.findIndex((c) => c.dimension === drilledDimension);

    // Everything ABOVE this dimension in the path stays; everything below it is
    // dropped, because it was scoped to the value we just navigated away from.
    const basePath = existingIndex >= 0 ? drillDownPath.slice(0, existingIndex) : drillDownPath;

    const newContext: DrillDownContext = {
      dimension: drilledDimension,
      value: dimensionValue,
      parentContext: basePath.length > 0 ? basePath[basePath.length - 1] : undefined,
    };
    const newPath = [...basePath, newContext];
    setDrillDownPath(newPath);
    applyDrillFilters(syncFiltersToPath(newPath));
  };

  const handleBreadcrumbClick = (index: number) => {
    // Navigate back in drill-down path
    if (index === -1) {
      // Go back to root. Only the drill-down filters are cleared - filters the
      // user added themselves in the filter panel survive, since "All" means
      // "no drill-down", not "discard my filters".
      setDrillDownPath([]);
      applyDrillFilters(syncFiltersToPath([]));
    } else {
      // Go back to specific level
      const newPath = drillDownPath.slice(0, index + 1);
      setDrillDownPath(newPath);
      applyDrillFilters(syncFiltersToPath(newPath));
    }
  };

  if (isLoading) {
    return <LoadingState message="Loading cost data..." />;
  }

  if (error || !costData) {
    return (
      <ErrorState
        title="Failed to load cost data"
        message="Unable to fetch cost explorer data. Please try again."
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-end gap-2">

        {/* Export button - AC 3: Export functionality */}
        <ExportButton data={costData} dimensionLabel={getDimensionLabel(selectedDimension)} />
        {/* withFreshData sends Cache-Control: no-cache so this is a real
            refetch - cost responses are cached server-side for an hour. */}
        <button
          onClick={() => withFreshData(async () => { await refetch(); })}
          disabled={isLoading}
          title="Refresh cost data"
          className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted/50 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
          {isLoading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Cloud Provider tiles - prominent, top-of-page, same treatment as Dashboard */}
      {/* Provider selector - shared component so Cost Explorer,
          Recommendations, Anomalies and the Dashboard cannot drift apart. */}
      <ProviderTiles value={selectedProvider} onChange={setSelectedProvider} />

      {/* Breadcrumb navigation for drill-down - AC 1 */}
      {drillDownPath.length > 0 && (
        <nav className="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
          <button
            onClick={() => handleBreadcrumbClick(-1)}
            className="text-primary hover:text-primary dark:text-primary dark:hover:text-primary"
          >
            All
          </button>
          {drillDownPath.map((context, index) => (
            // Keyed by dimension, not index: there is now exactly one step per
            // dimension, so the dimension IS the stable identity of the step.
            <React.Fragment key={String(context.dimension)}>
              <span className="text-muted-foreground/70">/</span>
              <button
                onClick={() => handleBreadcrumbClick(index)}
                className="text-primary hover:text-primary dark:text-primary dark:hover:text-primary"
              >
                {/* Name the dimension. With one step per dimension the trail can
                    read "AmazonEC2 / us-east-1", which does not say which part
                    is the service and which the region. */}
                <span className="text-muted-foreground">{getDimensionLabel(context.dimension)}:</span>{' '}
                {context.value}
              </button>
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* Controls */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Date range and granularity - AC 2 */}
        <div className="rounded-lg border border-border bg-card p-4 dark:border-border dark:bg-card">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Time Period
          </h3>
          <div>
            <label className="mb-1 block text-xs font-medium text-foreground/80">
              Date Range
            </label>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => setDateRange(update)}
            />
          </div>
          <div className="mt-3">
            <label
              htmlFor="granularity"
              className="mb-1 block text-xs font-medium text-foreground/80"
            >
              Granularity
            </label>
            <select
              id="granularity"
              value={granularity}
              onChange={(e) => setGranularity(e.target.value as TimeGranularity)}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
            >
              <option value={TimeGranularity.HOURLY}>Hourly</option>
              <option value={TimeGranularity.DAILY}>Daily</option>
              <option value={TimeGranularity.WEEKLY}>Weekly</option>
              <option value={TimeGranularity.MONTHLY}>Monthly</option>
              <option value={TimeGranularity.QUARTERLY}>Quarterly</option>
            </select>
          </div>
        </div>

        {/* Dimension and cost view selectors - AC 1, AC 4 */}
        <div className="rounded-lg border border-border bg-card p-4 dark:border-border dark:bg-card">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Analysis Options
          </h3>
          <div className="space-y-3">
            <div>
              <label
                htmlFor="dimension"
                className="mb-1 block text-xs font-medium text-foreground/80"
              >
                Group By Dimension
              </label>
              <GroupedDropdown
                id="dimension"
                value={selectedDimension}
                onChange={(v) => setSelectedDimension(v as CostDimension)}
                ariaLabel="Group by dimension"
                groups={[
                  {
                    options: Object.values(CostDimension)
                      .filter((dim) => dim !== CostDimension.CLOUD)
                      .map((dim) => ({ value: dim, label: getDimensionLabel(dim) })),
                  },
                  ...(costAllocationTags.length > 0
                    ? [
                        {
                          label: 'Cost Allocation Tags',
                          options: costAllocationTags.map((tag) => ({
                            value: `tag:${tag}`,
                            label: `Tag: ${tag}`,
                          })),
                        },
                      ]
                    : []),
                ]}
              />
            </div>
            <div>
              <label
                htmlFor="cost-view"
                className="mb-1 block text-xs font-medium text-foreground/80"
              >
                Cost View
              </label>
              <select
                id="cost-view"
                value={costView}
                onChange={(e) => setCostView(e.target.value as CostView)}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
              >
                {/* Net Amortized and Effective now use real AWS CUR
                    amortization logic (getCostColumnForView in
                    s3-data.service.ts) - they read identically to Amortized
                    today only because neither tenant has negotiated
                    enterprise discounts, not because the backend fakes them. */}
                {Object.values(CostView).map((view) => (
                  <option key={view} value={view}>
                    {getCostViewLabel(view)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="sort-order"
                className="mb-1 block text-xs font-medium text-foreground/80"
              >
                Sort By
              </label>
              <select
                id="sort-order"
                value={`${sortConfig.field}:${sortConfig.direction}`}
                onChange={(e) => {
                  const [field, direction] = e.target.value.split(':');
                  setSortConfig({ field, direction: direction as SortDirection });
                }}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
              >
                <option value={`cost:${SortDirection.DESC}`}>Cost (High to Low)</option>
                <option value={`cost:${SortDirection.ASC}`}>Cost (Low to High)</option>
                <option value={`dimensionValue:${SortDirection.ASC}`}>Name (A to Z)</option>
                <option value={`dimensionValue:${SortDirection.DESC}`}>Name (Z to A)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Apply Filters Button */}
      <div className="flex justify-end">
        <Button type="button" onClick={handleApply} disabled={!startDate || !endDate}>
          Apply
        </Button>
      </div>

      {/* Cost Summary Metrics */}
      {costSummary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total Cost" value={formatCurrencyValue(costSummary.totalCost)} icon={DollarSign} accent="primary" />
          <StatCard label="Avg Daily Cost" value={formatCurrencyValue(costSummary.averageDailyCost)} icon={Calendar} accent="primary" />
          <StatCard label="Projected Monthly" value={formatCurrencyValue(costSummary.projectedMonthlyCost)} icon={ArrowRightLeft} accent="primary" />
          {costSummary.changeAvailable ? (
            <StatCard
              label="Change"
              value={`${costSummary.changePercentage > 0 ? '+' : ''}${costSummary.changePercentage.toFixed(1)}%`}
              icon={costSummary.changeFromPreviousPeriod < 0 ? TrendingDown : TrendingUp}
              accent={costSummary.changeFromPreviousPeriod < 0 ? 'success' : 'danger'}
            />
          ) : (
            <Card className="p-4 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-muted-foreground/70 dark:bg-secondary dark:text-muted-foreground">
                <ArrowRightLeft className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-medium text-muted-foreground">Change</div>
                <div
                  className="mt-0.5 text-sm font-medium text-muted-foreground/70"
                  title="The prior-period comparison couldn't be retrieved - this doesn't mean spend was unchanged"
                >
                  Comparison unavailable
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Filter Panel - AC 3 */}
      <FilterPanel
        filters={filters}
        onFiltersChange={setFilters}
        availableDimensions={Object.values(CostDimension)}
      />

      {/* Cost Chart - AC 2: Time series visualization */}
      <CostChart data={costData.timeSeries} granularity={appliedFilters.granularity} currency={costData.currency} />

      {/* Dimension Breakdown - AC 1: Multi-dimensional group-by with drill-down.
          Sorting applies instantly on the already-fetched data (AC 3) rather
          than requiring another API round-trip or an Apply click - sortConfig
          was previously wired end-to-end but never actually used to order
          anything rendered on screen. */}
      <DimensionBreakdown
        data={[...costData.byDimension].sort((a, b) => {
          const dir = sortConfig.direction === SortDirection.ASC ? 1 : -1;
          if (sortConfig.field === 'dimensionValue') {
            return dir * a.dimensionValue.localeCompare(b.dimensionValue);
          }
          return dir * (a.cost - b.cost);
        })}
        dimension={selectedDimension}
        currency={costData.currency}
        onDrillDown={handleDrillDown}
      />

      {/* Metadata */}
      <div className="rounded-lg border border-border bg-muted/30 p-4 text-xs text-muted-foreground dark:border-border dark:bg-card dark:text-muted-foreground/70">
        <div className="flex flex-wrap gap-4">
          <span>Query time: {costData.metadata.queryTime.toFixed(0)}ms</span>
          <span>Data freshness: {new Date(costData.metadata.dataFreshness).toLocaleString()}</span>
          <span>Records: {costData.metadata.recordCount.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};
